import { ChangeEvent, Fragment, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  createIndustrialCostProductionVolume,
  deleteIndustrialCostProductionVolume,
  getIndustrialCostProducts,
  getIndustrialCostProductionVolumes,
  listIndustrialCostProductionLinesConfig,
  updateIndustrialCostProductionVolume,
} from '../services/industrialCostService';
import {
  analyzeFileNormalization,
  reviewFileNormalization,
  commitFileNormalization,
} from '../fileNormalization/service';
import type { FileNormalizationAnalyzeResult } from '../fileNormalization/types';
import FileNormalizationReviewPanel from '../fileNormalization/components/FileNormalizationReviewPanel';
import {
  applyProductionImportConfig,
  applyProductionImportMapping,
  applyProductionLineResolution,
  canContinueProductionImport,
  canGoToProductionReview,
  getProductionUnresolvedLineRefs,
  confirmProductionImport,
  productionShrinkWarning,
  createProductionImportFlowState,
  runProductionImportPreview,
} from '../importFlows/productionImportFlow';
import IndustrialCostSectionPlaceholder from '../components/IndustrialCostSectionPlaceholder';
import type {
  CreateProductionVolumePayload,
  UpdateProductionVolumePayload,
  ProductionDataQuality,
  ProductionSourceType,
  ProductionLineConfig,
  ProductionUploadMode,
  ProductionVolume,
  SourceGranularity,
} from '../types/industrialCost';
import { IMPORT_PERMISSION_DENIED_TEXT, useCanManageImports } from '../utils/importPermission';

import { esNumberFormat } from '../utils/numberFormat';
function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const maybeResponse = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeResponse.response?.data?.detail) {
      return maybeResponse.response.data.detail;
    }

    if (maybeResponse.message) {
      return maybeResponse.message;
    }
  }

  return 'Se produjo un error inesperado.';
}

function formatAmount(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return esNumberFormat({
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(value);
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('es-ES').format(date);
}

function formatGranularityLabel(value: SourceGranularity | null | undefined): string {
  if (value === 'day') return 'Diaria';
  if (value === 'month') return 'Mensual';
  if (value === 'year') return 'Anual';
  return '—';
}

function formatDeclaredPeriod(
  year: string | number | null | undefined,
  month: string | number | null | undefined,
): string {
  if (year === null || year === undefined || String(year).trim() === '') {
    return '—';
  }

  const normalizedYear = String(year).trim();
  const normalizedMonth = month === null || month === undefined ? '' : String(month).trim();

  if (!normalizedMonth) {
    return normalizedYear;
  }

  return `${normalizedYear}-${normalizedMonth.padStart(2, '0')}`;
}

const MONTH_OPTIONS = [
  { value: '1', label: 'Enero' },
  { value: '2', label: 'Febrero' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Mayo' },
  { value: '6', label: 'Junio' },
  { value: '7', label: 'Julio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

function formatMonthName(value: string | number | null | undefined): string {
  const normalized = value === null || value === undefined ? '' : String(value);
  return MONTH_OPTIONS.find((month) => month.value === normalized)?.label ?? '—';
}

function formatQualityLabel(value: ProductionDataQuality): string {
  if (value === 'high') return 'Alta';
  if (value === 'medium') return 'Media';
  if (value === 'low') return 'Baja';
  return value;
}

function formatSourceTypeLabel(value: ProductionSourceType): string {
  if (value === 'manual') return 'Manual';
  if (value === 'import') return 'Importación';
  if (value === 'derived') return 'Derivado';
  return value;
}

type ProductionFormState = {
  product_id: string;
  period_year: string;
  period_month: string;
  kg_produced: string;
  units_produced: string;
  data_quality: ProductionDataQuality;
  source_type: ProductionSourceType;
  source_ref: string;
  notes: string;
};

const initialForm: ProductionFormState = {
  product_id: '',
  period_year: '2025',
  period_month: '',
  kg_produced: '',
  units_produced: '',
  data_quality: 'high',
  source_type: 'manual',
  source_ref: '',
  notes: '',
};

const productionFieldLabels: Record<string, string> = {
  PRODUCTO: 'Producto',
  FECHA: 'Fecha',
  LINEA: 'Línea de producción',
  AÑO: 'Año',
  MES: 'Mes',
  KG: 'Kg',
  UNIDADES: 'Cantidad fabricada',
  UNIDADES_ADICIONALES: 'Cantidad fabricada adicional',
  CALIDAD: 'Calidad',
  REFERENCIA: 'Referencia',
  CLIENTE: 'Cliente',
  OF: 'Orden de fabricación',
};

const productionFieldOrderAggregated = [
  'PRODUCTO',
  'KG',
  'UNIDADES',
  'CALIDAD',
  'REFERENCIA',
] as const;

const productionFieldOrderDay = [
  'PRODUCTO',
  'FECHA',
  'LINEA',
  'KG',
  'UNIDADES',
  'UNIDADES_ADICIONALES',
  'CLIENTE',
  'OF',
  'CALIDAD',
  'REFERENCIA',
] as const;

type ImportStep = 'file' | 'mapping' | 'product_resolution' | 'review';

const importSteps: Array<{ key: ImportStep; label: string }> = [
  { key: 'file', label: 'Archivo' },
  { key: 'mapping', label: 'Mapeo' },
  { key: 'product_resolution', label: 'Referencias' },
  { key: 'review', label: 'Confirmación' },
];

type IndustrialCostProductionViewProps = {
  importOnly?: boolean;
  initialImportFile?: File | null;
  onImportClose?: () => void;
  onImportSuccess?: (message: string) => void;
};

export default function IndustrialCostProductionView({
  importOnly = false,
  initialImportFile = null,
  onImportClose,
  onImportSuccess,
}: IndustrialCostProductionViewProps = {}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(
    importOnly && initialImportFile !== null,
  );
  const [selectedYear, setSelectedYear] = useState<string>('2025');
  const [selectedMonth, setSelectedMonth] = useState<string>('1');
  const [productFilter, setProductFilter] = useState<'all' | string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'ok' | 'warning'>('all');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [form, setForm] = useState<ProductionFormState>(initialForm);
  const [editingVolumeId, setEditingVolumeId] = useState<string | null>(null);
  const [volumeToDelete, setVolumeToDelete] = useState<ProductionVolume | null>(null);

  const [importFlow, setImportFlow] = useState(
    createProductionImportFlowState(
      importOnly ? initialImportFile : null,
      importOnly
        ? {
            sourceGranularity: 'day',
            declaredPeriodYear: '',
            declaredPeriodMonth: '',
            uploadMode: 'replace_by_date_range',
          }
        : {
            sourceGranularity: 'month',
            declaredPeriodYear: '2025',
            declaredPeriodMonth: '',
            uploadMode: 'replace_by_period_range',
          },
    ),
  );

  const importFile = importFlow.file;
  const importUploadMode = importFlow.config.uploadMode;
  const importSourceGranularity = importFlow.config.sourceGranularity;
  const declaredPeriodYear = importFlow.config.declaredPeriodYear;
  const declaredPeriodMonth = importFlow.config.declaredPeriodMonth;

  const [importStep, setImportStep] = useState<ImportStep>('file');

  const [preparingInitialFile, setPreparingInitialFile] =
    useState(importOnly && initialImportFile !== null);

  const [initialFileError, setInitialFileError] =
    useState<string | null>(null);

  const [fileNormalization, setFileNormalization] =
    useState<FileNormalizationAnalyzeResult | null>(null);

  const [selectedImportSheet, setSelectedImportSheet] =
    useState('');

  const [selectedHeaderRow, setSelectedHeaderRow] =
    useState('1');

  const [normalizationDirty, setNormalizationDirty] =
    useState(false);

  const [reviewingNormalization, setReviewingNormalization] =
    useState(false);

  const [
    savingNormalizationProfile,
    setSavingNormalizationProfile,
  ] = useState(false);

  useEffect(() => {
    if (!importOnly || !initialImportFile) {
      return;
    }

    let cancelled = false;

    const prepareFile = async () => {
      setPreparingInitialFile(true);
      setInitialFileError(null);

      try {
        const normalization = await analyzeFileNormalization(
          initialImportFile,
          'production_volumes',
        );

        if (cancelled) return;

        setFileNormalization(normalization);
        setSelectedImportSheet(normalization.selected_sheet ?? '');
        setSelectedHeaderRow(
          String(normalization.header_row_index + 1),
        );
        setNormalizationDirty(false);

        setImportFlow({
          ...createProductionImportFlowState(initialImportFile, {
            sourceGranularity: 'day',
            declaredPeriodYear: '',
            declaredPeriodMonth: '',
            uploadMode: 'replace_by_date_range',
          }),
          mapping: normalization.applied_column_mapping ?? {},
          normalizationSheetName: normalization.selected_sheet,
          normalizationHeaderRowIndex:
            normalization.header_row_index,
        });

        setImportStep('file');
      } catch (error) {
        if (cancelled) return;

        setInitialFileError(getErrorMessage(error));
      } finally {
        if (!cancelled) {
          setPreparingInitialFile(false);
        }
      }
    };

    void prepareFile();

    return () => {
      cancelled = true;
    };
  }, [importOnly, initialImportFile]);

  const rawPreview = importFlow.preview;
  const mappingPreview =
    importStep === 'product_resolution' || importStep === 'review'
      ? importFlow.preview
      : null;

  const draftMapping = importFlow.mapping;

  const [productionQuery, productsQuery] = useQueries({
    queries: [
      {
        queryKey: ['industrial-cost-production-volumes'],
        queryFn: getIndustrialCostProductionVolumes,
        enabled: !importOnly,
      },
      {
        queryKey: ['industrial-cost-products'],
        queryFn: getIndustrialCostProducts,
        enabled: !importOnly,
      },
    ],
  });

  const productionLinesQuery = useQuery<ProductionLineConfig[]>({
    queryKey: ['industrial-cost-production-config-lines-active'],
    queryFn: listIndustrialCostProductionLinesConfig,
    staleTime: 60_000,
  });

  const productionVolumes = productionQuery.data ?? [];
  const products = productsQuery.data ?? [];

  const activeProductionLines = useMemo(
    () =>
      (productionLinesQuery.data ?? [])
        .filter((line) => line.is_active)
        .sort(
          (a, b) =>
            a.display_order - b.display_order ||
            a.name.localeCompare(b.name),
        ),
    [productionLinesQuery.data],
  );

  type EffectiveProductionRow = {
    productId: string;
    productName: string;
    productCode: string;
    kgProduced: number;
    unitsProduced: number;
    status: 'ok' | 'warning' | 'missing';
    observation: string;
    sourceUsed: 'day' | 'month' | null;
    hasDailyDetail: boolean;
    dailyRows: ProductionVolume[];
    monthlyRows: ProductionVolume[];
    annualRows: ProductionVolume[];
    actionableRows: ProductionVolume[];
    monthlyKgLoaded: number;
    monthlyUnitsLoaded: number;
    dailyKgAggregated: number;
    dailyUnitsAggregated: number;
  };

  const allMonthlyEffectiveRows = useMemo<EffectiveProductionRow[]>(() => {
    const year = Number(selectedYear);
    const month = Number(selectedMonth);

    if (Number.isNaN(year) || Number.isNaN(month) || month < 1 || month > 12) {
      return [];
    }

    const rowsByProduct = new Map<string, EffectiveProductionRow>();

    const normalizeDateMonth = (value: string | null | undefined) => {
      if (!value) return null;
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return null;
      return {
        year: parsed.getUTCFullYear(),
        month: parsed.getUTCMonth() + 1,
      };
    };

    products.forEach((product) => {
      const productRows = productionVolumes.filter((row) => row.product_id === product.id);

      const dailyRows = productRows.filter((row) => {
        if (row.source_granularity !== 'day') return false;
        const normalized = normalizeDateMonth(row.production_date);
        return normalized?.year === year && normalized?.month === month;
      });

      const monthlyRows = productRows.filter(
        (row) =>
          row.source_granularity === 'month' &&
          row.period_year === year &&
          row.period_month === month,
      );

      const annualRows = productRows.filter(
        (row) => row.source_granularity === 'year' && row.period_year === year,
      );

      const dailyKgAggregated = dailyRows.reduce(
        (sum, row) => sum + Number(row.kg_produced || 0),
        0,
      );
      const dailyUnitsAggregated = dailyRows.reduce(
        (sum, row) => sum + Number(row.units_produced || 0),
        0,
      );

      const monthlyKgLoaded = monthlyRows.reduce(
        (sum, row) => sum + Number(row.kg_produced || 0),
        0,
      );
      const monthlyUnitsLoaded = monthlyRows.reduce(
        (sum, row) => sum + Number(row.units_produced || 0),
        0,
      );

      let kgProduced = 0;
      let unitsProduced = 0;
      let status: 'ok' | 'warning' | 'missing' = 'missing';
      let observation = 'Sin producción cargada para el mes';
      let sourceUsed: 'day' | 'month' | null = null;

      if (dailyRows.length > 0) {
        kgProduced = dailyKgAggregated;
        unitsProduced = dailyUnitsAggregated;
        status = 'ok';
        sourceUsed = 'day';
        observation = 'Producción mensual consolidada desde detalle diario';

        const kgDiff = Math.abs(dailyKgAggregated - monthlyKgLoaded);
        const unitsDiff = Math.abs(dailyUnitsAggregated - monthlyUnitsLoaded);

        if (monthlyRows.length > 0) {
          if (kgDiff > 0.001 || unitsDiff > 0.001) {
            status = 'warning';
            observation =
              'Existen datos diarios y mensuales simultáneos; se usa el detalle diario';
          } else {
            observation =
              'Existen datos diarios y mensuales coherentes; se usa el detalle diario';
          }
        }
      } else if (monthlyRows.length > 0) {
        kgProduced = monthlyKgLoaded;
        unitsProduced = monthlyUnitsLoaded;
        status = 'ok';
        sourceUsed = 'month';
        observation = 'Producción mensual disponible';
      } else if (annualRows.length > 0) {
        const annualKg = annualRows.reduce((sum, row) => sum + Number(row.kg_produced || 0), 0);
        const annualUnits = annualRows.reduce((sum, row) => sum + Number(row.units_produced || 0), 0);
        kgProduced = annualKg / 12;
        unitsProduced = annualUnits / 12;
        status = 'warning';
        sourceUsed = 'month';
        observation = 'Dato anual distribuido entre 12 meses';
      }

      const actionableRows =
        dailyRows.length > 0
          ? dailyRows
          : monthlyRows.length > 0
            ? monthlyRows
            : annualRows;

      rowsByProduct.set(product.id, {
        productId: product.id,
        productName: product.name,
        productCode: product.code,
        kgProduced,
        unitsProduced,
        status,
        observation,
        sourceUsed,
        hasDailyDetail: dailyRows.length > 0,
        dailyRows,
        monthlyRows,
        annualRows,
        actionableRows,
        monthlyKgLoaded,
        monthlyUnitsLoaded,
        dailyKgAggregated,
        dailyUnitsAggregated,
      });
    });

    return Array.from(rowsByProduct.values()).sort((a, b) =>
      a.productName.localeCompare(b.productName),
    );
  }, [productionVolumes, products, selectedMonth, selectedYear]);

  const monthlyEffectiveRows = useMemo(() => {
    return allMonthlyEffectiveRows.filter((row) => {
      if (row.status === 'missing') return false;

      if (productFilter !== 'all' && row.productId !== productFilter) return false;

      const search = searchText.trim().toLowerCase();
      if (search) {
        const haystack = `${row.productName} ${row.productCode}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }

      if (statusFilter !== 'all' && row.status !== statusFilter) return false;

      return true;
    });
  }, [allMonthlyEffectiveRows, productFilter, searchText, statusFilter]);

  const productionKpis = useMemo(() => {
    const rowsWithProduction = allMonthlyEffectiveRows.filter(
      (row) => row.status !== 'missing' && (row.kgProduced > 0 || row.unitsProduced > 0),
    );

    return {
      totalKg: rowsWithProduction.reduce((sum, row) => sum + row.kgProduced, 0),
      totalUnits: rowsWithProduction.reduce((sum, row) => sum + row.unitsProduced, 0),
      productsWithProduction: rowsWithProduction.length,
      productsWithoutProduction: allMonthlyEffectiveRows.filter((row) => row.status === 'missing').length,
    };
  }, [allMonthlyEffectiveRows]);

  const rowsWithWarnings = useMemo(
    () => allMonthlyEffectiveRows.filter(
      (row) => row.status === 'warning' && row.observation !== 'Dato anual distribuido entre 12 meses'
    ).length,
    [allMonthlyEffectiveRows],
  );

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return monthlyEffectiveRows.slice(start, start + rowsPerPage);
  }, [monthlyEffectiveRows, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchText, productFilter, statusFilter, selectedYear, selectedMonth, rowsPerPage]);

  const toggleRowExpanded = (productId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  const createMutation = useMutation({
    mutationFn: (payload: CreateProductionVolumePayload) =>
      createIndustrialCostProductionVolume(payload),
    onSuccess: async () => {
      enqueueSnackbar('Producción registrada correctamente.', { variant: 'success' });
      setCreateOpen(false);
      setEditingVolumeId(null);
      setForm(initialForm);
      await queryClient.invalidateQueries({
        queryKey: ['industrial-cost-production-volumes'],
      });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      volumeId,
      payload,
    }: {
      volumeId: string;
      payload: UpdateProductionVolumePayload;
    }) => updateIndustrialCostProductionVolume(volumeId, payload),
    onSuccess: async () => {
      enqueueSnackbar('Producción actualizada correctamente.', { variant: 'success' });
      setCreateOpen(false);
      setEditingVolumeId(null);
      setForm(initialForm);
      await queryClient.invalidateQueries({
        queryKey: ['industrial-cost-production-volumes'],
      });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteIndustrialCostProductionVolume,
    onSuccess: async () => {
      enqueueSnackbar('Producción eliminada correctamente.', { variant: 'success' });
      setVolumeToDelete(null);
      await queryClient.invalidateQueries({
        queryKey: ['industrial-cost-production-volumes'],
      });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const previewImportMutation = {
    isPending: importFlow.status === 'loading',
    mutate: async ({
      file,
      mapping,
    }: {
      file: File;
      mapping?: Record<string, string | null>;
    }) => {
      setImportFlow((prev) => ({
        ...prev,
        status: 'loading',
        error: null,
        resultMessage: null,
      }));

      const nextState = await runProductionImportPreview(importFlow, {
        file,
        mapping,
      });

      setImportFlow(nextState);

      if (mapping) {
        if (!canContinueProductionImport(nextState)) {
          setImportStep('mapping');
          enqueueSnackbar(
            'El mapeo todavía no es válido. Revisa PRODUCTO y las magnitudes antes de continuar.',
            { variant: 'warning' },
          );
          return;
        }

        if (
          importOnly &&
          fileNormalization &&
          nextState.preview
        ) {
          try {
            await commitFileNormalization({
              importer_key: 'production_volumes',
              sheet_name:
                nextState.normalizationSheetName,
              header_row_index:
                nextState.normalizationHeaderRowIndex ?? 0,
              data_start_row_index:
                (nextState.normalizationHeaderRowIndex ?? 0) + 1,
              column_mapping: Object.fromEntries(
                Object.entries(nextState.mapping).filter(
                  (
                    entry,
                  ): entry is [string, string] =>
                    typeof entry[1] === 'string' &&
                    entry[1].trim().length > 0,
                ),
              ),
              known_columns:
                nextState.preview.columns,
            });
          } catch (error) {
            setImportStep('mapping');
            enqueueSnackbar(
              getErrorMessage(error),
              { variant: 'error' },
            );
            return;
          }
        }

        setImportStep('product_resolution');
        enqueueSnackbar('Mapeo confirmado correctamente.', { variant: 'success' });
        return;
      }

      setImportStep('mapping');
      enqueueSnackbar('Archivo analizado correctamente.', { variant: 'success' });
    },
  };

  const confirmImportMutation = {
    isPending: importFlow.status === 'confirming',
    mutate: async () => {
      if (!importFile) {
        throw new Error('Debes seleccionar un archivo antes de confirmar.');
      }
      // IND-053: un fichero con muchas menos filas que las que sustituye se confirma antes.
      const shrink = productionShrinkWarning(importFlow.preview, importFlow.config.uploadMode);
      if (shrink && !window.confirm(shrink)) return;

      setImportFlow((prev) => ({
        ...prev,
        status: 'confirming',
        error: null,
        resultMessage: null,
      }));

      const nextState = await confirmProductionImport(importFlow);
      setImportFlow(nextState);

      if (nextState.status === 'imported' || nextState.status === 'imported_with_issues') {
        enqueueSnackbar(nextState.resultMessage ?? 'Importación completada.', {
          variant: nextState.status === 'imported_with_issues' ? 'warning' : 'success',
        });

        try {
          await commitFileNormalization({
            importer_key: 'production_volumes',
            sheet_name: importFlow.normalizationSheetName,
            header_row_index: importFlow.normalizationHeaderRowIndex ?? 0,
            data_start_row_index: (importFlow.normalizationHeaderRowIndex ?? 0) + 1,
            column_mapping: Object.fromEntries(
              Object.entries(importFlow.mapping).filter(([, v]) => v !== null) as [string, string][],
            ),
            known_columns: importFlow.preview?.columns ?? [],
          });
        } catch {
          // El commit de normalización no es bloqueante
        }

        setImportOpen(false);
        setImportFlow(
          createProductionImportFlowState(null, {
            sourceGranularity: 'month',
            declaredPeriodYear: '2025',
            declaredPeriodMonth: '',
            uploadMode: 'replace_by_period_range',
          }),
        );
        setImportStep('file');

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['industrial-cost-production-volumes'],
          }),
          queryClient.invalidateQueries({
            queryKey: ['industrial-cost-uploads'],
          }),
        ]);

        if (importOnly) {
          onImportSuccess?.(
            nextState.resultMessage ?? 'Importación completada.',
          );
        }

        return;
      }

      if (nextState.error) {
        enqueueSnackbar(nextState.error, { variant: 'error' });
      }
    },
  };

  const isLoading = productionQuery.isLoading || productsQuery.isLoading;
  const isError = productionQuery.isError || productsQuery.isError;
  const error = productionQuery.error ?? productsQuery.error;

  function buildFormFromVolume(volume: ProductionVolume): ProductionFormState {
    return {
      product_id: volume.product_id,
      period_year: String(volume.period_year),
      period_month: volume.period_month ? String(volume.period_month) : '',
      kg_produced:
        volume.kg_produced === null || volume.kg_produced === undefined
          ? ''
          : String(volume.kg_produced),
      units_produced:
        volume.units_produced === null || volume.units_produced === undefined
          ? ''
          : String(volume.units_produced),
      data_quality: volume.data_quality,
      source_type: volume.source_type,
      source_ref: volume.source_ref ?? '',
      notes: volume.notes ?? '',
    };
  }

  function openCreateDialog() {
    setEditingVolumeId(null);
    setForm({
      ...initialForm,
      period_year: selectedYear,
      period_month: selectedMonth,
    });
    setCreateOpen(true);
  }

  function openEditDialog(volume: ProductionVolume) {
    setEditingVolumeId(volume.id);
    setForm(buildFormFromVolume(volume));
    setCreateOpen(true);
  }

  function closeCreateDialog() {
    if (createMutation.isPending || updateMutation.isPending) return;
    setCreateOpen(false);
    setEditingVolumeId(null);
    setForm(initialForm);
  }

  const handleChange = <K extends keyof ProductionFormState>(
    key: K,
    value: ProductionFormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    const payload: CreateProductionVolumePayload | UpdateProductionVolumePayload = {
      product_id: form.product_id,
      production_date: null,
      period_year: Number(form.period_year),
      period_month: form.period_month ? Number(form.period_month) : null,
      source_granularity: form.period_month ? 'month' : 'year',
      kg_produced: form.kg_produced ? Number(form.kg_produced) : null,
      units_produced: form.units_produced ? Number(form.units_produced) : null,
      data_quality: form.data_quality,
      source_type: form.source_type,
      source_ref: form.source_ref.trim() || null,
      notes: form.notes.trim() || null,
    };

    if (editingVolumeId) {
      updateMutation.mutate({
        volumeId: editingVolumeId,
        payload,
      });
      return;
    }

    createMutation.mutate(payload);
  };

  const canSubmit =
    form.product_id.length > 0 &&
    form.period_year.trim().length > 0 &&
    !Number.isNaN(Number(form.period_year)) &&
    (form.kg_produced.trim().length > 0 || form.units_produced.trim().length > 0) &&
    (form.kg_produced.trim().length === 0 || !Number.isNaN(Number(form.kg_produced))) &&
    (form.units_produced.trim().length === 0 || !Number.isNaN(Number(form.units_produced)));

  // IND-007
  const canImport = useCanManageImports();

  const handleImportFileChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFile = event.target.files?.[0] ?? null;

    event.target.value = '';

    setFileNormalization(null);
    setSelectedImportSheet('');
    setSelectedHeaderRow('1');
    setNormalizationDirty(false);
    setInitialFileError(null);
    setPreparingInitialFile(Boolean(selectedFile));
    setImportStep('file');

    let normalization: FileNormalizationAnalyzeResult | null = null;

    if (selectedFile) {
      try {
        normalization = await analyzeFileNormalization(
          selectedFile,
          'production_volumes',
        );

        setFileNormalization(normalization);
        setSelectedImportSheet(
          normalization.selected_sheet ?? '',
        );
        setSelectedHeaderRow(
          String(normalization.header_row_index + 1),
        );
      } catch (error) {
        setInitialFileError(getErrorMessage(error));
      } finally {
        setPreparingInitialFile(false);
      }
    }

    setImportFlow({
      ...createProductionImportFlowState(selectedFile, {
        sourceGranularity: importSourceGranularity,
        declaredPeriodYear,
        declaredPeriodMonth,
        uploadMode: importUploadMode,
      }),
      mapping: normalization?.applied_column_mapping ?? {},
      normalizationSheetName:
        normalization?.selected_sheet ?? null,
      normalizationHeaderRowIndex:
        normalization?.header_row_index ?? null,
    });
  };

  const handleApplySheetAndHeader = async () => {
    if (!importFile) return;

    const headerRowNumber = Number(selectedHeaderRow);

    if (
      !Number.isInteger(headerRowNumber) ||
      headerRowNumber < 1
    ) {
      setInitialFileError(
        'La fila de cabecera debe ser un número entero mayor o igual que 1.',
      );
      return;
    }

    if (
      fileNormalization?.sheets.length &&
      !selectedImportSheet
    ) {
      setInitialFileError(
        'Debes seleccionar una hoja del archivo.',
      );
      return;
    }

    setReviewingNormalization(true);
    setInitialFileError(null);

    try {
      const reviewed = await reviewFileNormalization(
        importFile,
        'production_volumes',
        headerRowNumber - 1,
        selectedImportSheet || null,
      );

      setFileNormalization(reviewed);
      setSelectedImportSheet(reviewed.selected_sheet ?? '');
      setSelectedHeaderRow(
        String(reviewed.header_row_index + 1),
      );
      setNormalizationDirty(false);

      setImportFlow((current) => ({
        ...current,
        preview: null,
        mapping: reviewed.applied_column_mapping ?? {},
        productResolution: {},
        lineResolution: {},
        normalizationSheetName: reviewed.selected_sheet,
        normalizationHeaderRowIndex:
          reviewed.header_row_index,
        status: 'idle',
        error: null,
        resultMessage: null,
      }));

      setImportStep('file');
    } catch (error) {
      setInitialFileError(getErrorMessage(error));
    } finally {
      setReviewingNormalization(false);
    }
  };

  const handleImportOnlyNormalizationSheetChange = async (
    sheetName: string,
  ) => {
    if (!importFile || !sheetName) return;

    setReviewingNormalization(true);
    setInitialFileError(null);

    try {
      const reviewed = await reviewFileNormalization(
        importFile,
        'production_volumes',
        0,
        sheetName,
      );

      setFileNormalization(reviewed);
      setSelectedImportSheet(
        reviewed.selected_sheet ?? '',
      );
      setSelectedHeaderRow(
        String(reviewed.header_row_index + 1),
      );
      setNormalizationDirty(false);

      setImportFlow({
        ...createProductionImportFlowState(
          importFile,
          {
            sourceGranularity: 'day',
            declaredPeriodYear: '',
            declaredPeriodMonth: '',
            uploadMode: 'replace_by_date_range',
          },
        ),
        mapping:
          reviewed.applied_column_mapping ?? {},
        normalizationSheetName:
          reviewed.selected_sheet,
        normalizationHeaderRowIndex:
          reviewed.header_row_index,
      });
    } catch (error) {
      setInitialFileError(getErrorMessage(error));
    } finally {
      setReviewingNormalization(false);
    }
  };

  const handleUseImportOnlyNormalization = async () => {
    if (!importFile || !fileNormalization) return;

    const headerRowNumber = Number(
      selectedHeaderRow,
    );

    if (
      !Number.isInteger(headerRowNumber) ||
      headerRowNumber < 1
    ) {
      setInitialFileError(
        'Selecciona visualmente una fila válida como cabecera.',
      );
      return;
    }

    setSavingNormalizationProfile(true);
    setInitialFileError(null);

    try {
      let finalNormalization =
        fileNormalization;

      const selectedHeaderRowIndex =
        headerRowNumber - 1;

      const structureChanged =
        selectedImportSheet !==
          (fileNormalization.selected_sheet ?? '') ||
        selectedHeaderRowIndex !==
          fileNormalization.header_row_index;

      if (structureChanged) {
        finalNormalization =
          await reviewFileNormalization(
            importFile,
            'production_volumes',
            selectedHeaderRowIndex,
            selectedImportSheet || null,
          );

        setFileNormalization(
          finalNormalization,
        );
        setSelectedImportSheet(
          finalNormalization.selected_sheet ?? '',
        );
        setSelectedHeaderRow(
          String(
            finalNormalization.header_row_index + 1,
          ),
        );
        setNormalizationDirty(false);
      }

      await commitFileNormalization({
        importer_key: 'production_volumes',
        sheet_name:
          finalNormalization.selected_sheet,
        header_row_index:
          finalNormalization.header_row_index,
        data_start_row_index:
          finalNormalization.header_row_index + 1,
        column_mapping:
          finalNormalization.applied_column_mapping ?? {},
        known_columns:
          finalNormalization.detected_columns,
      });

      const baseFlow = {
        ...createProductionImportFlowState(
          importFile,
          {
            sourceGranularity: 'day',
            declaredPeriodYear: '',
            declaredPeriodMonth: '',
            uploadMode: 'replace_by_date_range',
          },
        ),
        mapping:
          finalNormalization.applied_column_mapping ?? {},
        normalizationSheetName:
          finalNormalization.selected_sheet,
        normalizationHeaderRowIndex:
          finalNormalization.header_row_index,
      };

      const nextState =
        await runProductionImportPreview(
          baseFlow,
          {
            file: importFile,
            mapping:
              finalNormalization.applied_column_mapping ?? {},
          },
        );

      setImportFlow(nextState);

      if (nextState.status === 'error') {
        setInitialFileError(
          nextState.error ??
            'No se pudo analizar el archivo de producción.',
        );
        return;
      }

      setImportStep('mapping');

      enqueueSnackbar(
        'Estructura guardada. Revisa ahora el mapeo.',
        { variant: 'success' },
      );
    } catch (error) {
      setInitialFileError(
        getErrorMessage(error),
      );
    } finally {
      setSavingNormalizationProfile(false);
    }
  };

  const handleOpenImport = () => {
    setImportOpen(true);
  };

  const handleCloseImport = () => {
    if (previewImportMutation.isPending || confirmImportMutation.isPending) {
      return;
    }

    setImportOpen(false);

    if (importOnly) {
      onImportClose?.();
      return;
    }

    setImportFlow(
      createProductionImportFlowState(null, {
        sourceGranularity: 'month',
        declaredPeriodYear: '2025',
        declaredPeriodMonth: '',
        uploadMode: 'replace_by_period_range',
      }),
    );
    setImportStep('file');
  };

  const missingRequiredMapping = useMemo(() => {
    return rawPreview?.missing_required_fields ?? [];
  }, [rawPreview]);

  const existingProductRefs = useMemo(
    () =>
      mappingPreview?.detected_product_refs.filter(
        (item) => item.is_resolved,
      ) ?? [],
    [mappingPreview],
  );

  const newProductRefs = useMemo(
    () =>
      mappingPreview?.detected_product_refs.filter(
        (item) => !item.is_resolved,
      ) ?? [],
    [mappingPreview],
  );

  const rowsWithProductRef =
    mappingPreview?.coverage_summary.rows_with_product_ref ?? 0;

  const detectedLineRefs =
    mappingPreview?.detected_line_refs ?? [];

  const unresolvedLineRefs = useMemo(
    () => getProductionUnresolvedLineRefs(importFlow),
    [importFlow],
  );

  const resolvedLineRefsCount =
    detectedLineRefs.length - unresolvedLineRefs.length;

  const activeStepIndex = useMemo(() => {
    return importSteps.findIndex((step) => step.key === importStep);
  }, [importStep]);

  const activeProductionFieldOrder = useMemo(() => {
    return importSourceGranularity === 'day'
      ? productionFieldOrderDay
      : productionFieldOrderAggregated;
  }, [importSourceGranularity]);

  const canAnalyzeImportFile = useMemo(() => {
    if (!importFile || previewImportMutation.isPending) {
      return false;
    }

    if (importSourceGranularity === 'day') {
      return true;
    }

    if (importSourceGranularity === 'month') {
      return (
        declaredPeriodYear.trim().length > 0 &&
        declaredPeriodMonth.trim().length > 0
      );
    }

    return declaredPeriodYear.trim().length > 0;
  }, [
    importFile,
    previewImportMutation.isPending,
    importSourceGranularity,
    declaredPeriodYear,
    declaredPeriodMonth,
  ]);

  const canConfirmMapping =
    !!importFile &&
    !!rawPreview &&
    missingRequiredMapping.length === 0 &&
    !previewImportMutation.isPending;

  const canGoToReview = canGoToProductionReview(importFlow);

  return (
    <>
      {!importOnly && (
        <>
          <Stack spacing={3}>
            <Paper
              elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Stack spacing={1.25}>
            <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
              Producción
            </Typography>

            <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.7 }}>
              Producción mensual por producto. Esta pantalla consolida el dato útil del periodo,
              prioriza el detalle diario cuando existe y señala incidencias que pueden afectar al
              coste derivado.
            </Typography>

            <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
              El objetivo operativo es verificar qué productos tienen base de fabricación suficiente
              antes de derivar consumos técnicos y coste industrial.
            </Typography>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            px: 3,
            py: 3,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 240px' },
              gap: 2.5,
              alignItems: 'stretch',
            }}
          >
            <Stack spacing={2.25}>
              <Stack spacing={0.5}>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  Cobertura de producción
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  Periodo {formatMonthName(selectedMonth)} {selectedYear}. La cobertura se calcula
                  sobre todos los productos, no solo sobre los filtros visibles.
                </Typography>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' },
                  gap: 1.25,
                }}
              >
                {[
                  { label: 'Kg fabricados', value: formatAmount(productionKpis.totalKg) },
                  { label: 'Unidades fabricadas', value: formatAmount(productionKpis.totalUnits) },
                  { label: 'Con producción', value: productionKpis.productsWithProduction },
                  { label: 'Sin producción', value: productionKpis.productsWithoutProduction },
                  { label: 'Incidencias', value: rowsWithWarnings },
                ].map((item) => (
                  <Paper
                    key={item.label}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid rgba(148,163,184,0.14)',
                      bgcolor: 'rgba(2,6,23,0.26)',
                      p: 1.6,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 950, fontSize: '1.35rem', mt: 0.4 }}>
                      {item.value}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '160px 200px' },
                  gap: 1.5,
                }}
              >
                <TextField
                  select
                  label="Año"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  fullWidth
                  size="small"
                >
                  {[2023, 2024, 2025, 2026].map((year) => (
                    <MenuItem key={year} value={String(year)}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Mes"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  fullWidth
                  size="small"
                >
                  {MONTH_OPTIONS.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Stack>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(56,189,248,0.18)',
                bgcolor: 'rgba(8,47,73,0.16)',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 1.25,
              }}
            >
              <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>Acciones</Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.55 }}>
                Alta manual o carga masiva de producción.
              </Typography>

              <Button
                variant="outlined"
                onClick={handleOpenImport}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  borderColor: 'rgba(56,189,248,0.4)',
                  color: '#e2e8f0',
                }}
              >
                Importar producción
              </Button>

              <Button
                variant="contained"
                onClick={openCreateDialog}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  bgcolor: '#38bdf8',
                  color: '#0f172a',
                  '&:hover': { bgcolor: '#38bdf8', filter: 'brightness(1.06)' },
                }}
              >
                Nueva producción
              </Button>
            </Paper>
          </Box>
        </Paper>

        {rowsWithWarnings > 0 ? (
          <Alert severity="warning">
            Hay <strong>{rowsWithWarnings}</strong> productos con incidencias de producción en el
            mes seleccionado. La aplicación solo muestra estos avisos cuando afectan al dato mensual utilizable.
          </Alert>
        ) : null}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', xl: 'minmax(220px, 0.8fr) minmax(0, 1.2fr)' },
                gap: 2,
                alignItems: 'end',
              }}
            >
              <Stack spacing={0.5}>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  Producción mensual efectiva
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  Producción consolidada por producto para el periodo seleccionado.
                </Typography>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr 0.8fr' },
                  gap: 1.25,
                }}
              >
                <TextField
                  label="Buscar"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  fullWidth
                  size="small"
                />

                <Autocomplete
                  options={products}
                  value={products.find((product) => product.id === productFilter) ?? null}
                  getOptionLabel={(option) => `${option.code ? `${option.code} · ` : ''}${option.name}`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_, value) => setProductFilter(value?.id ?? 'all')}
                  renderInput={(params) => (
                    <TextField {...params} label="Producto" size="small" />
                  )}
                />

                <TextField
                  select
                  label="Estado"
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as 'all' | 'ok' | 'warning')
                  }
                  fullWidth
                  size="small"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="ok">Correcto</MenuItem>
                  <MenuItem value="warning">Revisar</MenuItem>
                </TextField>
              </Box>
            </Box>
          </Box>

          {isError ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{getErrorMessage(error)}</Alert>
            </Box>
          ) : isLoading ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Cargando producción...
              </Typography>
            </Box>
          ) : productionVolumes.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <IndustrialCostSectionPlaceholder
                title="Sin producción todavía"
                description="Registra la primera producción o usa la importación para cargarla de forma masiva."
              />
            </Box>
          ) : monthlyEffectiveRows.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <IndustrialCostSectionPlaceholder
                title="Sin producción visible para el periodo"
                description="No hay producciones cargadas que cumplan los filtros actuales. Los productos sin producción se mantienen en la cobertura, pero no se muestran en la tabla operativa."
              />
            </Box>
          ) : (
            <>
              <Box sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#94a3b8', fontWeight: 800, width: 56 }} />
                      <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Producto</TableCell>
                      <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                        Kg fabricados
                      </TableCell>
                      <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                        Unidades fabricadas
                      </TableCell>
                      <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Estado</TableCell>
                      <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Observaciones</TableCell>
                      <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedRows.map((row) => {
                      const isExpanded = !!expandedRows[row.productId];
                      const canExpand =
                        row.hasDailyDetail ||
                        row.monthlyRows.length > 0 ||
                        row.annualRows.length > 0;

                      return (
                        <Fragment key={row.productId}>
                          <TableRow hover>
                            <TableCell>
                              {canExpand ? (
                                <IconButton
                                  size="small"
                                  onClick={() => toggleRowExpanded(row.productId)}
                                  sx={{ color: '#cbd5e1' }}
                                >
                                  {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              ) : null}
                            </TableCell>

                            <TableCell sx={{ color: '#f8fafc', fontWeight: 700 }}>
                              <Stack spacing={0.25}>
                                <Typography sx={{ fontWeight: 700 }}>{row.productName}</Typography>
                                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                  {row.productCode}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                              {formatAmount(row.kgProduced)}
                            </TableCell>

                            <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                              {formatAmount(row.unitsProduced)}
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                label={
                                  row.status === 'ok'
                                    ? 'Correcto'
                                    : row.status === 'warning'
                                      ? 'Revisar'
                                      : 'Sin producción'
                                }
                                color={
                                  row.status === 'ok'
                                    ? 'success'
                                    : row.status === 'warning'
                                      ? 'warning'
                                      : 'default'
                                }
                                variant={row.status === 'missing' ? 'outlined' : 'filled'}
                              />
                            </TableCell>

                            <TableCell sx={{ color: '#cbd5e1' }}>{row.observation}</TableCell>

                            <TableCell align="right">
                              {row.actionableRows.length === 1 ? (
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => openEditDialog(row.actionableRows[0])}
                                    sx={{ textTransform: 'none', fontWeight: 800 }}
                                  >
                                    Editar
                                  </Button>

                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setVolumeToDelete(row.actionableRows[0])}
                                    sx={{
                                      textTransform: 'none',
                                      fontWeight: 800,
                                      borderColor: 'rgba(248,113,113,0.42)',
                                      color: '#fecaca',
                                    }}
                                  >
                                    Eliminar
                                  </Button>
                                </Stack>
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => toggleRowExpanded(row.productId)}
                                  sx={{ textTransform: 'none', fontWeight: 800 }}
                                >
                                  Ver líneas
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>

                          {canExpand ? (
                            <TableRow>
                              <TableCell colSpan={7} sx={{ p: 0, borderBottom: 0 }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box
                                    sx={{
                                      px: 3,
                                      py: 2.25,
                                      bgcolor: 'rgba(2,6,23,0.32)',
                                      borderTop: '1px solid rgba(148,163,184,0.12)',
                                    }}
                                  >
                                    <Stack spacing={2}>
                                      {row.hasDailyDetail ? (
                                        <Box>
                                          <Typography
                                            variant="body2"
                                            sx={{ color: '#f8fafc', fontWeight: 800, mb: 1 }}
                                          >
                                            Detalle diario utilizado
                                          </Typography>

                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell>Fecha</TableCell>
                                                <TableCell align="right">Kg</TableCell>
                                                <TableCell align="right">Unidades</TableCell>
                                                <TableCell align="right">Acciones</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {row.dailyRows.map((dailyRow) => (
                                                <TableRow key={dailyRow.id}>
                                                  <TableCell>{formatDate(dailyRow.production_date)}</TableCell>
                                                  <TableCell align="right">
                                                    {formatAmount(dailyRow.kg_produced)}
                                                  </TableCell>
                                                  <TableCell align="right">
                                                    {formatAmount(dailyRow.units_produced)}
                                                  </TableCell>
                                                  <TableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                      <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => openEditDialog(dailyRow)}
                                                        sx={{ textTransform: 'none', fontWeight: 800 }}
                                                      >
                                                        Editar
                                                      </Button>
                                                      <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => setVolumeToDelete(dailyRow)}
                                                        sx={{
                                                          textTransform: 'none',
                                                          fontWeight: 800,
                                                          borderColor: 'rgba(248,113,113,0.42)',
                                                          color: '#fecaca',
                                                        }}
                                                      >
                                                        Eliminar
                                                      </Button>
                                                    </Stack>
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      ) : null}

                                      {row.monthlyRows.length > 0 ? (
                                        <Box>
                                          <Alert severity="info" sx={{ mb: 1.5 }}>
                                            Valor mensual cargado para el periodo: Kg{' '}
                                            <strong>{formatAmount(row.monthlyKgLoaded)}</strong> ·
                                            Unidades <strong>{formatAmount(row.monthlyUnitsLoaded)}</strong>
                                          </Alert>

                                          {row.monthlyRows.length > 1 ? (
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow>
                                                  <TableCell>Origen</TableCell>
                                                  <TableCell align="right">Kg</TableCell>
                                                  <TableCell align="right">Unidades</TableCell>
                                                  <TableCell align="right">Acciones</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {row.monthlyRows.map((monthlyRow) => (
                                                  <TableRow key={monthlyRow.id}>
                                                    <TableCell>
                                                      {formatSourceTypeLabel(monthlyRow.source_type)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatAmount(monthlyRow.kg_produced)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatAmount(monthlyRow.units_produced)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        <Button
                                                          size="small"
                                                          variant="outlined"
                                                          onClick={() => openEditDialog(monthlyRow)}
                                                          sx={{ textTransform: 'none', fontWeight: 800 }}
                                                        >
                                                          Editar
                                                        </Button>
                                                        <Button
                                                          size="small"
                                                          variant="outlined"
                                                          onClick={() => setVolumeToDelete(monthlyRow)}
                                                          sx={{
                                                            textTransform: 'none',
                                                            fontWeight: 800,
                                                            borderColor: 'rgba(248,113,113,0.42)',
                                                            color: '#fecaca',
                                                          }}
                                                        >
                                                          Eliminar
                                                        </Button>
                                                      </Stack>
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          ) : null}
                                        </Box>
                                      ) : null}

                                      {row.hasDailyDetail && row.monthlyRows.length > 0 ? (
                                        <Alert severity={row.status === 'warning' ? 'warning' : 'success'}>
                                          La aplicación toma el detalle diario como base del mes.
                                        </Alert>
                                      ) : null}

                                      {row.annualRows.length > 0 ? (
                                        <Alert severity="info">
                                          Dato anual cargado para {selectedYear}: Kg{' '}
                                          <strong>
                                            {formatAmount(row.annualRows.reduce((s, r) => s + Number(r.kg_produced || 0), 0))}
                                          </strong>{' '}
                                          · Unidades{' '}
                                          <strong>
                                            {formatAmount(row.annualRows.reduce((s, r) => s + Number(r.units_produced || 0), 0))}
                                          </strong>
                                          . Se distribuye entre 12 meses para el cálculo.
                                        </Alert>
                                      ) : null}
                                    </Stack>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>

              <TablePagination
                component="div"
                count={monthlyEffectiveRows.length}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(Number(event.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                  color: '#cbd5e1',
                  borderTop: '1px solid rgba(148,163,184,0.14)',
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    color: '#94a3b8',
                  },
                  '.MuiSvgIcon-root': { color: '#cbd5e1' },
                }}
              />
            </>
          )}
        </Paper>
      </Stack>

      <Dialog
        open={createOpen}
        onClose={closeCreateDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editingVolumeId ? 'Editar producción' : 'Nueva producción'}</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={products}
              value={products.find((product) => product.id === form.product_id) ?? null}
              getOptionLabel={(option) => `${option.code ? `${option.code} · ` : ''}${option.name}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => handleChange('product_id', value?.id ?? '')}
              renderInput={(params) => (
                <TextField {...params} label="Producto" size="small" required />
              )}
            />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 1.5,
              }}
            >
              <TextField
                label="Año"
                type="number"
                value={form.period_year}
                onChange={(e) => handleChange('period_year', e.target.value)}
                fullWidth
                size="small"
              />

              <TextField
                select
                label="Mes"
                value={form.period_month}
                onChange={(e) => handleChange('period_month', e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="">Sin mes · anual</MenuItem>
                {MONTH_OPTIONS.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 1.5,
              }}
            >
              <TextField
                label="Kg producidos"
                type="number"
                value={form.kg_produced}
                onChange={(e) => handleChange('kg_produced', e.target.value)}
                fullWidth
                size="small"
              />

              <TextField
                label="Unidades producidas"
                type="number"
                value={form.units_produced}
                onChange={(e) => handleChange('units_produced', e.target.value)}
                fullWidth
                size="small"
              />
            </Box>

            <TextField
              select
              label="Calidad del dato"
              value={form.data_quality}
              onChange={(e) =>
                handleChange('data_quality', e.target.value as ProductionDataQuality)
              }
              fullWidth
              size="small"
            >
              <MenuItem value="high">{formatQualityLabel('high')}</MenuItem>
              <MenuItem value="medium">{formatQualityLabel('medium')}</MenuItem>
              <MenuItem value="low">{formatQualityLabel('low')}</MenuItem>
            </TextField>

            <TextField
              select
              label="Origen"
              value={form.source_type}
              onChange={(e) =>
                handleChange('source_type', e.target.value as ProductionSourceType)
              }
              fullWidth
              size="small"
            >
              <MenuItem value="manual">{formatSourceTypeLabel('manual')}</MenuItem>
              <MenuItem value="import">{formatSourceTypeLabel('import')}</MenuItem>
              <MenuItem value="derived">{formatSourceTypeLabel('derived')}</MenuItem>
            </TextField>

            <TextField
              label="Referencia de origen"
              value={form.source_ref}
              onChange={(e) => handleChange('source_ref', e.target.value)}
              fullWidth
              size="small"
            />

            <TextField
              label="Notas"
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={closeCreateDialog}
            disabled={createMutation.isPending || updateMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit || createMutation.isPending || updateMutation.isPending}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#38bdf8',
              color: '#0f172a',
              '&:hover': { bgcolor: '#38bdf8', filter: 'brightness(1.06)' },
            }}
          >
            {createMutation.isPending || updateMutation.isPending
              ? 'Guardando...'
              : editingVolumeId
                ? 'Guardar cambios'
                : 'Crear producción'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(volumeToDelete)}
        onClose={() => !deleteMutation.isPending && setVolumeToDelete(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Eliminar producción</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
              Esta acción eliminará el registro de producción seleccionado y puede cambiar la base de
              derivación de consumos técnicos del periodo.
            </Typography>

            {volumeToDelete ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(248,113,113,0.24)',
                  bgcolor: 'rgba(127,29,29,0.12)',
                  p: 2,
                }}
              >
                <Stack spacing={0.5}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                    {products.find((product) => product.id === volumeToDelete.product_id)?.name ?? 'Producto'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    Periodo: {formatDeclaredPeriod(volumeToDelete.period_year, volumeToDelete.period_month)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    Kg: {formatAmount(volumeToDelete.kg_produced)} · Unidades:{' '}
                    {formatAmount(volumeToDelete.units_produced)}
                  </Typography>
                </Stack>
              </Paper>
            ) : null}

            <Alert severity="warning" variant="outlined">
              Si solo necesitas corregir el dato, usa Editar en lugar de eliminar.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setVolumeToDelete(null)}
            disabled={deleteMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => volumeToDelete && deleteMutation.mutate(volumeToDelete.id)}
            disabled={!volumeToDelete || deleteMutation.isPending}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#ef4444',
              color: '#fff',
              '&:hover': { bgcolor: '#dc2626' },
            }}
          >
            {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar producción'}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}

      <Dialog
        open={importOpen}
        onClose={handleCloseImport}
        fullWidth
        maxWidth={importOnly ? 'xl' : 'lg'}
      >
        <DialogTitle>
          {importOnly ? 'Configurar producción real' : 'Importar producción'}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {preparingInitialFile ? (
              <Alert severity="info">
                Analizando la hoja y la cabecera del archivo seleccionado...
              </Alert>
            ) : null}

            {initialFileError ? (
              <Alert severity="error">
                {initialFileError}
              </Alert>
            ) : null}

            <Stepper activeStep={activeStepIndex} alternativeLabel>
              {importSteps.map((step) => (
                <Step key={step.key}>
                  <StepLabel>{step.label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {importStep === 'file' && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Paso 1 · Archivo
                  </Typography>

                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    {importOnly
                      ? 'Comprueba la hoja y selecciona visualmente la fila de encabezados. Esta fuente se carga siempre como producción diaria con fecha por fila.'
                      : 'Selecciona el archivo y el modo de carga. Después analiza el archivo para detectar sus columnas.'}
                  </Typography>

                  {importOnly ? (
                    <Alert severity="info" variant="outlined">
                      Archivo seleccionado: {importFile?.name ?? '—'}.
                      Para cambiarlo, utiliza la tarjeta de Producción real.
                    </Alert>
                  ) : (
                    <>
                    {!canImport ? (
                      <Typography sx={{ color: '#fbbf24', fontSize: 12 }}>
                        {IMPORT_PERMISSION_DENIED_TEXT}
                      </Typography>
                    ) : null}
                    <Button
                      variant="outlined"
                      component="label"
                      disabled={!canImport}
                      sx={{ textTransform: 'none' }}
                    >
                      {importFile
                        ? `Archivo seleccionado: ${importFile.name}`
                        : 'Seleccionar archivo'}
                      <input
                        hidden
                        type="file"
                        accept=".csv,.xlsx"
                        onChange={handleImportFileChange}
                      />
                    </Button>
                    </>
                  )}

                  {importOnly && fileNormalization ? (
                    <FileNormalizationReviewPanel
                      result={fileNormalization}
                      selectedSheetName={
                        selectedImportSheet
                      }
                      selectedHeaderRowIndex={Math.max(
                        0,
                        Number(selectedHeaderRow) - 1,
                      )}
                      isReviewing={
                        reviewingNormalization
                      }
                      isContinuing={
                        savingNormalizationProfile ||
                        previewImportMutation.isPending
                      }
                      onSheetChange={(sheetName) =>
                        void handleImportOnlyNormalizationSheetChange(
                          sheetName,
                        )
                      }
                      onHeaderRowChange={(rowIndex) => {
                        setSelectedHeaderRow(
                          String(rowIndex + 1),
                        );
                        setNormalizationDirty(true);
                      }}
                      onContinue={() =>
                        void handleUseImportOnlyNormalization()
                      }
                    />
                  ) : null}

                  {fileNormalization && !importOnly && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 800 }}
                        >
                          Hoja y cabecera
                        </Typography>

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              md: 'minmax(0, 2fr) minmax(0, 1fr) auto',
                            },
                            gap: 1.5,
                            alignItems: 'center',
                          }}
                        >
                          {fileNormalization.sheets.length > 0 ? (
                            <TextField
                              select
                              size="small"
                              label="Hoja"
                              value={selectedImportSheet}
                              onChange={(event) => {
                                setSelectedImportSheet(event.target.value);
                                setNormalizationDirty(true);
                              }}
                            >
                              {fileNormalization.sheets.map((sheet) => (
                                <MenuItem
                                  key={sheet.name}
                                  value={sheet.name}
                                >
                                  {sheet.name} · muestra: {sheet.row_count} filas
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            <Alert severity="info">
                              Archivo sin hojas Excel.
                            </Alert>
                          )}

                          <TextField
                            label="Fila de cabecera"
                            type="number"
                            size="small"
                            value={selectedHeaderRow}
                            onChange={(event) => {
                              setSelectedHeaderRow(event.target.value);
                              setNormalizationDirty(true);
                            }}
                            inputProps={{ min: 1 }}
                          />

                          <Button
                            variant="outlined"
                            disabled={
                              !normalizationDirty ||
                              reviewingNormalization ||
                              preparingInitialFile
                            }
                            onClick={() => {
                              void handleApplySheetAndHeader();
                            }}
                          >
                            {reviewingNormalization
                              ? 'Aplicando...'
                              : 'Aplicar'}
                          </Button>
                        </Box>

                        {normalizationDirty ? (
                          <Alert severity="warning">
                            Has cambiado la hoja o la cabecera.
                            Pulsa Aplicar antes de analizar el archivo.
                          </Alert>
                        ) : (
                          <Alert severity="info">
                            Hoja y cabecera aplicadas. El número de filas
                            mostrado en el selector corresponde a la
                            muestra de detección, no al archivo completo.
                          </Alert>
                        )}
                      </Stack>
                    </Paper>
                  )}

                  {!importOnly ? (
                    <>
                        <TextField
                          select
                          label="Granularidad del archivo"
                          value={importSourceGranularity}
                          onChange={async (e) => {
                            const next = e.target.value as SourceGranularity;
      
                            const nextState = await applyProductionImportConfig(importFlow, {
                              sourceGranularity: next,
                              uploadMode:
                                next === 'day'
                                  ? 'replace_by_date_range'
                                  : 'replace_by_period_range',
                              declaredPeriodMonth: next === 'day' || next === 'year'
                                ? ''
                                : importFlow.config.declaredPeriodMonth,
                            });
      
                            setImportFlow(nextState);
                          }}
                          fullWidth
                          size="small"
                        >
                          <MenuItem value="day">Diaria (con fecha real por fila)</MenuItem>
                          <MenuItem value="month">Mensual agregada</MenuItem>
                          <MenuItem value="year">Anual agregada</MenuItem>
                        </TextField>
      
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 1.5,
                          }}
                        >
                          <TextField
                            label="Año declarado"
                            type="number"
                            value={declaredPeriodYear}
                            onChange={async (e) => {
                              const nextState = await applyProductionImportConfig(importFlow, {
                                declaredPeriodYear: e.target.value,
                              });
                              setImportFlow(nextState);
                            }}
                            fullWidth
                            size="small"
                          />
      
                          <TextField
                            select
                            label="Mes declarado"
                            value={declaredPeriodMonth}
                            onChange={async (e) => {
                              const nextState = await applyProductionImportConfig(importFlow, {
                                declaredPeriodMonth: e.target.value,
                              });
                              setImportFlow(nextState);
                            }}
                            fullWidth
                            size="small"
                            disabled={importSourceGranularity !== 'month'}
                          >
                            <MenuItem value="">Sin mes</MenuItem>
                            {MONTH_OPTIONS.map((month) => (
                              <MenuItem key={month.value} value={month.value}>
                                {month.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
      
                        <TextField
                          select
                          label="Modo de carga"
                          value={importUploadMode}
                          onChange={async (e) => {
                            const nextState = await applyProductionImportConfig(importFlow, {
                              uploadMode: e.target.value as ProductionUploadMode,
                            });
                            setImportFlow(nextState);
                          }}
                          fullWidth
                          size="small"
                        >
                          <MenuItem value="replace">
                            Sustituir toda la producción existente
                          </MenuItem>
                          <MenuItem value="incremental">
                            Añadir sin borrar la producción existente
                          </MenuItem>
                          {importSourceGranularity === 'day' ? (
                            <MenuItem value="replace_by_date_range">
                              Sustituir solo los días que trae el fichero
                            </MenuItem>
                          ) : (
                            <MenuItem value="replace_by_period_range">
                              Sustituir solo el periodo detectado o declarado
                            </MenuItem>
                          )}
                        </TextField>
      
                        <Alert severity="info" variant="outlined">
                          {importSourceGranularity === 'day'
                            ? 'En granularidad diaria, FECHA será obligatoria en el mapeo.'
                            : importSourceGranularity === 'month'
                              ? 'En granularidad mensual, FECHA no es obligatoria. El periodo puede venir del archivo o declararse aquí.'
                              : 'En granularidad anual, FECHA no es obligatoria. El año puede venir del archivo o declararse aquí.'}
                        </Alert>
      
                        {/* IND-008 (D5/D11) */}
                        {importSourceGranularity !== 'day' ? (
                          <Alert severity="warning">
                            Esta producción no tiene fecha diaria: los materiales que consume quedarán sin stock calculado hasta cargar producción diaria.
                          </Alert>
                        ) : null}

                        {importSourceGranularity === 'month' &&
                        declaredPeriodMonth.trim().length === 0 ? (
                          <Alert severity="warning">
                            Para importación mensual agregada debes informar el mes en este paso.
                          </Alert>
                        ) : null}
      
                        {importSourceGranularity === 'year' &&
                        declaredPeriodYear.trim().length === 0 ? (
                          <Alert severity="warning">
                            Para importación anual agregada debes informar el año en este paso.
                          </Alert>
                        ) : null}
      
                        <Stack direction="row" justifyContent="flex-end">
                          <Button
                            variant="contained"
                            onClick={() =>
                              importFile &&
                              previewImportMutation.mutate({
                                file: importFile,
                              })
                            }
                            disabled={
                              !canAnalyzeImportFile ||
                              preparingInitialFile ||
                              reviewingNormalization ||
                              normalizationDirty ||
                              Boolean(initialFileError)
                            }
                            sx={{ textTransform: 'none', fontWeight: 800 }}
                          >
                            {previewImportMutation.isPending ? 'Analizando...' : 'Analizar archivo'}
                          </Button>
                        </Stack>
                    </>
                  ) : (
                    <Alert severity="info" variant="outlined">
                      Carga operativa predeterminada: producción diaria,
                      FECHA obligatoria por fila y sustitución únicamente
                      del rango real de fechas detectado en el archivo.
                    </Alert>
                  )}
                </Stack>
              </Paper>
            )}

            {importStep === 'mapping' && rawPreview && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Paso 2 · Mapeo de columnas
                  </Typography>

                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Ajusta el significado de cada columna y confirma el mapeo. El contexto temporal ya se ha definido en el paso anterior.
                  </Typography>

                  {missingRequiredMapping.length > 0 && (
                    <Alert severity="warning">
                      Faltan campos obligatorios por asignar: {missingRequiredMapping.join(', ')}
                    </Alert>
                  )}

                  {missingRequiredMapping.length === 0 ? (
                    <Alert severity="info">
                      {importSourceGranularity === 'day'
                        ? 'En granularidad diaria, FECHA se usa para derivar el periodo.'
                        : `El periodo de esta carga queda fijado como ${formatDeclaredPeriod(
                            declaredPeriodYear,
                            declaredPeriodMonth,
                          )}.`}
                    </Alert>
                  ) : null}

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 1.5,
                    }}
                  >
                    {activeProductionFieldOrder.map((fieldKey) => (
                      <TextField
                        select
                        label={productionFieldLabels[fieldKey]}
                        value={draftMapping[fieldKey] ?? ''}
                        onChange={async (e) => {
                          const nextState = await applyProductionImportMapping(
                            importFlow,
                            fieldKey,
                            e.target.value,
                          );
                          setImportFlow(nextState);
                        }}
                        fullWidth
                        size="small"
                      >
                        <MenuItem value="">Sin asignar</MenuItem>
                        {rawPreview.columns.map((column) => (
                          <MenuItem key={column} value={column}>
                            {column}
                          </MenuItem>
                        ))}
                      </TextField>
                    ))}
                  </Box>

                  <Stack direction="row" justifyContent="space-between">
                    <Button
                      onClick={() => setImportStep('file')}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      Volver
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() =>
                        importFile &&
                        previewImportMutation.mutate({
                          file: importFile,
                          mapping: draftMapping,
                        })
                      }
                      disabled={!canConfirmMapping}
                      sx={{ textTransform: 'none', fontWeight: 800 }}
                    >
                      {previewImportMutation.isPending ? 'Confirmando...' : 'Confirmar mapeo'}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            )}

            {importStep === 'product_resolution' && mappingPreview && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 800 }}
                  >
                    Paso 3 · Referencias de producto
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    PROA utiliza la referencia del archivo como identidad
                    del producto. Las referencias nuevas se registrarán
                    automáticamente al confirmar la importación.
                  </Typography>

                  {rowsWithProductRef === 0 ? (
                    <Alert severity="error">
                      No se han detectado referencias de producto válidas.
                      Revisa el mapeo antes de continuar.
                    </Alert>
                  ) : (
                    <>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                          },
                          gap: 1.5,
                        }}
                      >
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            Referencias existentes
                          </Typography>
                          <Typography variant="h5">
                            {existingProductRefs.length}
                          </Typography>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            Referencias nuevas
                          </Typography>
                          <Typography variant="h5">
                            {newProductRefs.length}
                          </Typography>
                        </Paper>
                      </Box>

                      {newProductRefs.length > 0 ? (
                        <Alert severity="info">
                          Se registrarán automáticamente{' '}
                          {newProductRefs.length} referencias nuevas.
                          No es necesario crear previamente estos productos.
                        </Alert>
                      ) : (
                        <Alert severity="success">
                          Todas las referencias detectadas ya existen
                          en PROA.
                        </Alert>
                      )}

                      {newProductRefs.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, fontWeight: 800 }}
                          >
                            Muestra de referencias nuevas
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            {newProductRefs.slice(0, 20).map((item) => (
                              <Chip
                                key={item.normalized_value}
                                variant="outlined"
                                label={`${item.source_value} · ${item.row_count} filas`}
                              />
                            ))}
                          </Stack>

                          {newProductRefs.length > 20 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 1 }}
                            >
                              Se muestran las primeras 20 referencias.
                            </Typography>
                          )}
                        </Paper>
                      )}
                    </>
                  )}

                  <Divider />

                  <Stack spacing={1.5}>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 900 }}
                      >
                        Correspondencias de líneas de producción
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        Los nombres del archivo se relacionan con las
                        líneas canónicas de planta. Las correspondencias
                        manuales se guardarán como aliases y se
                        reutilizarán en próximas cargas.
                      </Typography>
                    </Box>

                    {detectedLineRefs.length === 0 ? (
                      <Alert severity="info" variant="outlined">
                        El archivo no contiene una línea o máquina
                        informada. La producción se importará sin línea.
                      </Alert>
                    ) : (
                      <>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              sm: 'repeat(2, minmax(0, 1fr))',
                            },
                            gap: 1.5,
                          }}
                        >
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              Líneas detectadas
                            </Typography>
                            <Typography variant="h5">
                              {detectedLineRefs.length}
                            </Typography>
                          </Paper>

                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              Resueltas
                            </Typography>
                            <Typography variant="h5">
                              {resolvedLineRefsCount}
                            </Typography>
                          </Paper>
                        </Box>

                        {unresolvedLineRefs.length === 0 ? (
                          <Alert severity="success">
                            Todas las líneas del archivo están
                            relacionadas con la configuración de planta.
                          </Alert>
                        ) : (
                          <Alert severity="warning">
                            Debes asignar {unresolvedLineRefs.length}{' '}
                            nombre(s) de línea antes de continuar.
                          </Alert>
                        )}

                        <Stack spacing={1}>
                          {detectedLineRefs.map((item) => {
                            const selectedLineId =
                              importFlow.lineResolution[
                                item.source_value
                              ] ||
                              item.resolved_production_line_config_id ||
                              '';

                            return (
                              <Paper
                                key={item.normalized_value}
                                variant="outlined"
                                sx={{ p: 1.5 }}
                              >
                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                      xs: '1fr',
                                      md: 'minmax(0, 1fr) minmax(260px, 1fr)',
                                    },
                                    gap: 1.5,
                                    alignItems: 'center',
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      sx={{ fontWeight: 900 }}
                                    >
                                      {item.source_value}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {item.row_count} fila(s)
                                      {item.is_resolved &&
                                      item.resolved_production_line_name
                                        ? ` · ${item.resolved_production_line_name}`
                                        : ''}
                                    </Typography>
                                  </Box>

                                  <TextField
                                    select
                                    size="small"
                                    label="Línea de planta"
                                    value={selectedLineId}
                                    onChange={(event) => {
                                      setImportFlow(
                                        applyProductionLineResolution(
                                          importFlow,
                                          item.source_value,
                                          event.target.value,
                                        ),
                                      );
                                    }}
                                    disabled={
                                      item.is_resolved ||
                                      productionLinesQuery.isLoading
                                    }
                                  >
                                    <MenuItem value="">
                                      Sin asignar
                                    </MenuItem>

                                    {activeProductionLines.map(
                                      (line) => (
                                        <MenuItem
                                          key={line.id}
                                          value={line.id}
                                        >
                                          {line.code} · {line.name}
                                        </MenuItem>
                                      ),
                                    )}
                                  </TextField>
                                </Box>
                              </Paper>
                            );
                          })}
                        </Stack>
                      </>
                    )}
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Button
                      onClick={() => setImportStep('mapping')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                      }}
                    >
                      Volver
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() => setImportStep('review')}
                      disabled={!canGoToReview}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                      }}
                    >
                      Continuar
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            )}

            {importStep === 'review' && mappingPreview && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Paso 4 · Confirmación final
                  </Typography>

                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Revisa el resumen y confirma la importación de producción.
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                      gap: 1.5,
                    }}
                  >
                    <Alert severity="info">
                      Archivo: {importFile?.name || '—'}
                    </Alert>
                    <Alert severity="info">
                      Granularidad: {formatGranularityLabel(mappingPreview.source_granularity)}
                    </Alert>
                    <Alert severity="info">
                      Modo de carga: {importUploadMode}
                    </Alert>
                    <Alert severity="info">
                      {mappingPreview.source_granularity === 'day'
                        ? `Rango detectado: ${formatDate(mappingPreview.detected_date_min)} — ${formatDate(mappingPreview.detected_date_max)}`
                        : `Periodo declarado: ${formatDeclaredPeriod(
                            mappingPreview.declared_period_year,
                            mappingPreview.declared_period_month,
                          )}`}
                    </Alert>
                    <Alert severity="info">
                      Filas totales: {mappingPreview.coverage_summary.total_rows}
                    </Alert>
                    <Alert severity="info">
                      Con producto: {mappingPreview.coverage_summary.rows_with_product_ref}
                    </Alert>
                    <Alert severity="info">
                      Contexto temporal válido: {mappingPreview.coverage_summary.rows_with_period_year}
                    </Alert>
                    <Alert severity="info">
                      Con alguna magnitud: {mappingPreview.coverage_summary.rows_with_any_quantity}
                    </Alert>
                    <Alert severity="info">
                      Periodos detectados: {mappingPreview.detected_periods.join(', ') || '—'}
                    </Alert>
                    <Alert severity="info">
                      Referencias existentes: {existingProductRefs.length}
                    </Alert>

                    <Alert severity="info">
                      Referencias nuevas que se crearán automáticamente:{' '}
                      {newProductRefs.length}
                    </Alert>

                    <Alert
                      severity={
                        unresolvedLineRefs.length > 0
                          ? 'warning'
                          : 'info'
                      }
                    >
                      Líneas de producción: {resolvedLineRefsCount} de{' '}
                      {detectedLineRefs.length} resueltas.
                    </Alert>

                    {mappingPreview.coverage_summary.rows_without_product_ref > 0 && (
                      <Alert severity="warning">
                        Hay{' '}
                        {mappingPreview.coverage_summary.rows_without_product_ref}{' '}
                        fila(s) sin referencia de producto.
                        No podrán incorporarse a la producción.
                      </Alert>
                    )}
                  </Box>

                  <Stack direction="row" justifyContent="space-between">
                    <Button
                      onClick={() => setImportStep('product_resolution')}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      Volver
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() => {
                        // IND-002: confirmación explícita del modo de carga.
                        const modeText =
                          importUploadMode === 'replace'
                            ? 'Se BORRARÁ toda la producción existente y se sustituirá por la de este fichero.'
                            : importUploadMode === 'incremental'
                              ? 'Se AÑADIRÁ la producción de este fichero sin borrar la existente (puede duplicar días ya cargados).'
                              : importUploadMode === 'replace_by_date_range'
                                ? 'Se sustituirá la producción de los días que trae el fichero.'
                                : 'Se sustituirá la producción del periodo detectado o declarado.';
                        if (window.confirm(`${modeText} ¿Continuar?`)) {
                          confirmImportMutation.mutate();
                        }
                      }}
                      disabled={
                        confirmImportMutation.isPending ||
                        !mappingPreview ||
                        mappingPreview.coverage_summary.rows_with_product_ref === 0 ||
                        mappingPreview.coverage_summary.rows_with_any_quantity === 0
                      }
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        bgcolor: '#38bdf8',
                        color: '#0f172a',
                        '&:hover': { bgcolor: '#38bdf8', filter: 'brightness(1.06)' },
                      }}
                    >
                      {confirmImportMutation.isPending ? 'Importando...' : 'Confirmar importación'}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={handleCloseImport}
            disabled={previewImportMutation.isPending || confirmImportMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}